import React, { useState, useEffect, useRef, useCallback } from 'react';
import { AlertTriangle, Info, XCircle, CheckCircle2, X, ExternalLink } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TYPE_CONFIG = {
  info: { icon: Info, bg: 'bg-blue-500/10', border: 'border-blue-500/20', text: 'text-blue-300', iconColor: 'text-blue-400' },
  warning: { icon: AlertTriangle, bg: 'bg-amber-500/10', border: 'border-amber-500/20', text: 'text-amber-200', iconColor: 'text-amber-400' },
  critical: { icon: XCircle, bg: 'bg-red-500/10', border: 'border-red-500/20', text: 'text-red-200', iconColor: 'text-red-400' },
  success: { icon: CheckCircle2, bg: 'bg-emerald-500/10', border: 'border-emerald-500/20', text: 'text-emerald-200', iconColor: 'text-emerald-400' },
};

const AlertBanners = () => {
  const [alerts, setAlerts] = useState([]);
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('dismissed_alerts') || '[]'); } catch { return []; }
  });
  const containerRef = useRef(null);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/maintenance/alerts/active`).then(r => setAlerts(r.data || [])).catch(() => {});
  }, []);

  const updateCSSVar = useCallback(() => {
    const h = containerRef.current ? containerRef.current.offsetHeight : 0;
    document.documentElement.style.setProperty('--alert-banner-height', `${h}px`);
  }, []);

  const visible = alerts.filter(a => !dismissed.includes(a.id));

  useEffect(() => {
    updateCSSVar();
  }, [visible.length, updateCSSVar]);

  useEffect(() => {
    // Reset when no banners
    if (visible.length === 0) {
      document.documentElement.style.setProperty('--alert-banner-height', '0px');
    }
    return () => document.documentElement.style.setProperty('--alert-banner-height', '0px');
  }, [visible.length]);

  const dismiss = (id) => {
    const updated = [...dismissed, id];
    setDismissed(updated);
    sessionStorage.setItem('dismissed_alerts', JSON.stringify(updated));
  };

  if (visible.length === 0) return null;

  return (
    <div ref={containerRef} className="w-full fixed top-0 left-0 right-0 z-[60]" data-testid="alert-banners">
      {visible.map(alert => {
        const config = TYPE_CONFIG[alert.type] || TYPE_CONFIG.warning;
        const Icon = config.icon;
        return (
          <div key={alert.id} className={`${config.bg} border-b ${config.border} px-4 py-3 flex items-center justify-center gap-3`}>
            <Icon className={`w-4 h-4 ${config.iconColor} flex-shrink-0`} />
            <p className={`${config.text} text-sm font-medium`}>{alert.message}</p>
            {alert.link_text && alert.link_url && (
              <a href={alert.link_url} target="_blank" rel="noopener noreferrer"
                className={`${config.iconColor} text-xs font-semibold flex items-center gap-1 hover:underline`}>
                {alert.link_text} <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {alert.dismissible && (
              <button onClick={() => dismiss(alert.id)}
                data-testid={`dismiss-alert-${alert.id}`}
                className="text-slate-400 hover:text-white transition-colors ml-2 flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default AlertBanners;
